<template>
  <div>
    <!-- ===== MOBILE: danh sách card, tập trung SỐ SUẤT ĐĂNG KÝ ===== -->
    <div v-if="$vuetify.breakpoint.smAndDown">
      <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />
      <template v-for="(item, idx) in rows">
        <!-- Tiêu đề ngày (mở đầu mỗi ngày) -->
        <div
          v-if="item.firstOfDay || item.empty"
          :key="`day-${idx}`"
          class="mb-day px-3 py-2 mt-1 font-weight-bold"
          :class="{ 'mb-today': isToday(item) }"
        >
          {{ item.day | scheduleDay }}
          <span class="text--secondary caption ml-1">{{ item.date | ddmmyyyy }}</span>
        </div>
        <!-- Ngày không có bữa -->
        <div v-if="item.empty" :key="`empty-${idx}`" class="text--secondary caption text-center pb-2 pt-1">
          Không có bữa ăn
        </div>
        <!-- Card bữa ăn -->
        <v-card v-else :key="`card-${idx}`" outlined class="mx-3 mb-3 mt-2" :class="{ 'mb-card-today': isToday(item) }">
          <div class="d-flex align-center px-3 pt-2">
            <div class="flex-grow-1">
              <div class="font-weight-bold d-flex align-center">
                {{ item.slot.title }}
                <v-icon v-if="item.locks.length" small color="red" class="ml-1">mdi-lock</v-icon>
              </div>
              <div class="caption text--secondary">
                {{ mealTypeLabel(item.slot.type) }} · {{ timeOfDayLabel(item.slot.timeOfDay) }}
              </div>
            </div>
            <v-menu offset-y left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="editMenu(item)">
                  <v-icon small left>mdi-pencil</v-icon>
                  <v-list-item-title>Cập nhật thực đơn</v-list-item-title>
                </v-list-item>
                <v-list-item @click="goRegister(item)">
                  <v-icon small left>mdi-clipboard-text</v-icon>
                  <v-list-item-title>Đăng ký</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
          <!-- SỐ SUẤT ĐĂNG KÝ — ảnh bên trái, số + tên món bên phải (mỗi menu 1 hàng) -->
          <div class="px-3 pb-3 pt-2">
            <div class="mb-count d-flex align-center mb-2">
              <v-img
                v-if="mainImg(item.menu1Main)"
                :src="mainImg(item.menu1Main)"
                contain
                max-height="120"
                class="rounded mr-3"
                style="flex: 0 0 40%; max-width: 40%"
              />
              <div class="flex-grow-1 text-center" style="min-width: 0">
                <div class="mb-count-num">{{ item.count1 }}</div>
                <div class="caption text--secondary">
                  {{ item.slot.menuCount > 1 ? 'Thực đơn 1' : 'Suất đăng ký' }}
                </div>
                <div class="caption text-truncate">{{ mainText(item.menu1Main, item.menu1Side) }}</div>
              </div>
            </div>
            <div v-if="item.slot.menuCount > 1" class="mb-count d-flex align-center">
              <v-img
                v-if="mainImg(item.menu2Main)"
                :src="mainImg(item.menu2Main)"
                contain
                max-height="120"
                class="rounded mr-3"
                style="flex: 0 0 40%; max-width: 40%"
              />
              <div class="flex-grow-1 text-center" style="min-width: 0">
                <div class="mb-count-num">{{ item.count2 }}</div>
                <div class="caption text--secondary">Thực đơn 2</div>
                <div class="caption text-truncate">{{ mainText(item.menu2Main, item.menu2Side) }}</div>
              </div>
            </div>
          </div>
        </v-card>
      </template>
      <div v-if="!rows.length && !loading" class="text--secondary text-center py-6">Không có dữ liệu</div>
    </div>

    <!-- ===== DESKTOP: bảng đầy đủ ===== -->
    <v-data-table
      v-else
      loading-text="Đang Tải"
      hide-default-footer
      :headers="headers"
      :loading="loading"
      :items-per-page="-1"
      :items="rows"
      :item-class="rowClass"
      no-data-text="Không có dữ liệu"
      disable-sort
    >
      <!-- Thứ / Ngày: chỉ hiện ở dòng đầu của mỗi ngày -->
      <template v-slot:[`item.weekDay`]="{ item }">
        <div v-if="item.firstOfDay || item.empty" class="font-weight-bold">
          {{ item.day | scheduleDay }}
          <div class="text--secondary caption">{{ item.date | ddmmyyyy }}</div>
        </div>
      </template>

      <template v-slot:[`item.mealType`]="{ item }">
        <span v-if="!item.empty">{{ mealTypeLabel(item.slot.type) }}</span>
        <span v-else class="text--secondary">Không có bữa ăn</span>
      </template>
      <template v-slot:[`item.timeOfDay`]="{ item }">
        <span v-if="!item.empty">{{ timeOfDayLabel(item.slot.timeOfDay) }}</span>
      </template>
      <template v-slot:[`item.title`]="{ item }">
        <div v-if="!item.empty" class="d-flex align-center">
          <span>{{ item.slot.title }}</span>
          <v-tooltip v-if="item.locks.length" top>
            <template v-slot:activator="{ on }">
              <v-icon small color="red" class="ml-1 cursor-pointer" v-on="on">mdi-lock</v-icon>
            </template>
            <div v-for="(l, i) in item.locks" :key="i">
              Khóa nhóm <b>{{ groupTitle(l) }}</b
              >: {{ l.fromDate | ddmmyyyy }} → {{ l.toDate ? $options.filters.ddmmyyyy(l.toDate) : 'không xác định' }}
            </div>
          </v-tooltip>
        </div>
      </template>
      <template v-slot:[`item.menuCount`]="{ item }">
        <span v-if="!item.empty" class="text-center d-block">{{ item.slot.menuCount }}</span>
      </template>
      <template v-slot:[`item.maxPortion`]="{ item }">
        <span v-if="!item.empty" class="text-center d-block">{{ item.slot.maxPortion }}</span>
      </template>
      <template v-slot:[`item.fee`]="{ item }">
        <span v-if="!item.empty">{{
          item.slot.feeType === 'free' ? 'Miễn phí' : formatMoney(item.slot.feeAmount)
        }}</span>
      </template>

      <template v-slot:[`item.menu1`]="{ item }">
        <div v-if="!item.empty" class="d-flex align-center pa-2">
          <v-img
            v-if="mainImg(item.menu1Main)"
            :src="mainImg(item.menu1Main)"
            contain
            class="rounded mr-2"
            style="width: 60px; height: 60px; flex: 0 0 44px"
          />
          <div>
            <div class="caption success--text">
              <span class="font-weight-bold"> {{ item.count1 }}</span> suất đăng ký
            </div>
            <div v-if="item.menu1Main.length || item.menu1Side.length">
              <span v-if="item.menu1Main.length" class="dish-main">{{ names(item.menu1Main) }}</span
              ><span v-if="item.menu1Main.length && item.menu1Side.length">, </span
              >
              <span v-if="item.menu1Side.length" class="dish-side ">{{ names(item.menu1Side) }}</span>
            </div>
            <div v-else class="error--text caption">Chưa có món</div>
          </div>
        </div>
      </template>
      <template v-slot:[`item.menu2`]="{ item }">
        <div v-if="!item.empty && item.slot.menuCount > 1" class="d-flex align-center">
          <v-img
            v-if="mainImg(item.menu2Main)"
            :src="mainImg(item.menu2Main)"
            contain
            class="rounded mr-2"
            style="width: 44px; height: 44px; flex: 0 0 44px"
          />
          <div>
            <div class="caption success--text">
              <span class="font-weight-bold"> {{ item.count2 }} </span> suất đăng ký
            </div>
            <div v-if="item.menu2Main.length || item.menu2Side.length">
              <span v-if="item.menu2Main.length" class="dish-main">{{ names(item.menu2Main) }}</span
              ><span v-if="item.menu2Main.length && item.menu2Side.length">, </span
              ><span v-if="item.menu2Side.length" class="dish-side">{{ names(item.menu2Side) }}</span>
            </div>
            <div v-else class="error--text caption">Chưa có món</div>
          </div>
        </div>
      </template>

      <!-- Cột thao tác: ⋮ → Cập nhật (bữa 2 menu thì chọn TĐ1/TĐ2) + Đăng ký -->
      <template v-slot:[`item.actions`]="{ item }">
        <v-menu v-if="!item.empty" offset-y left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon small v-bind="attrs" v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn>
          </template>
          <v-list dense>
            <!-- Cập nhật: mở dialog (bữa 2 menu → dialog có 2 tab) -->
            <v-list-item @click="editMenu(item)">
              <v-icon small left>mdi-pencil</v-icon>
              <v-list-item-title>Cập nhật thực đơn</v-list-item-title>
            </v-list-item>
            <!-- Đăng ký -->
            <v-list-item @click="goRegister(item)">
              <v-icon small left>mdi-clipboard-text</v-icon>
              <v-list-item-title>Đăng ký</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>
    <FoodMenuEditDialog :target="editTarget" :state="editDialog" @saved="refresh" />
  </div>
</template>
<script>
import { mapActions, mapState } from 'vuex'
import moment from 'moment'
import { get } from 'lodash'
import { timeOfDayLabel, MEAL_TYPE_LABEL } from '@/modules/mealConfig/mealConfigConstants'

export default {
  components: {
    FoodMenuEditDialog: () => import('@/modules/foodMenu/FoodMenuEditDialog.vue')
  },
  data() {
    return {
      headers: [
        { text: 'Thứ / Ngày', value: 'weekDay', width: 120 },
        { text: 'Loại', value: 'mealType', width: 90 },
        { text: 'Thời điểm', value: 'timeOfDay', width: 90 },
        { text: 'Tên bữa', value: 'title' },
        { text: 'Số menu', value: 'menuCount', width: 80, align: 'center' },
        { text: 'Suất tối đa', value: 'maxPortion', width: 80, align: 'center' },
        { text: 'Biểu phí', value: 'fee', width: 110 },
        { text: 'Thực đơn 1', value: 'menu1' },
        { text: 'Thực đơn 2', value: 'menu2' },
        { text: 'Thao tác', value: 'actions', width: 90, align: 'center' }
      ],
      editDialog: false,
      editTarget: null
    }
  },
  async created() {
    await this.loadDefaultSlot()
    await this.refresh()
    this.$nextTick(() => this.scrollToToday())
  },
  computed: {
    ...mapState('FoodMenu', ['loading', 'rows'])
  },
  methods: {
    ...mapActions('FoodMenu', ['fetchData', 'loadDefaultSlot']),
    timeOfDayLabel,
    mealTypeLabel(v) {
      return MEAL_TYPE_LABEL[v] || v
    },
    async refresh() {
      await this.fetchData()
    },
    scrollToToday() {
      // bảng desktop dùng tr.today-row; card mobile dùng .mb-card-today
      const el = this.$el.querySelector('tr.today-row, .mb-card-today')
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    },
    isToday(item) {
      return item.date && moment(item.date).isSame(moment(), 'day')
    },
    rowClass(item) {
      // tô nền dòng mở đầu mỗi ngày để tách các thứ cho dễ nhìn; ngày hôm nay nổi bật hơn
      const classes = []
      if (item.firstOfDay || item.empty) classes.push('day-start-row')
      if (this.isToday(item)) classes.push('today-row')
      return classes.join(' ')
    },
    formatMoney(v) {
      return (v || 0).toLocaleString('vi-VN') + ' đ'
    },
    names(arr) {
      return (arr || []).map(d => d.name).join(', ')
    },
    mainImg(arr) {
      // ảnh đại diện = ảnh món chính đầu tiên (Strapi đã populate dish.image)
      return get(arr, '0.image.url', '')
    },
    mainText(mainArr, sideArr) {
      // tên món chính, nếu có món phụ thì thêm ", ..." (dùng cho mobile)
      const main = this.names(mainArr)
      if (!main) return ''
      return main + (sideArr && sideArr.length ? ', ...' : '')
    },
    groupTitle(lock) {
      return get(lock, 'meal_group.title', '—')
    },
    editMenu(item) {
      this.editTarget = {
        date: item.date,
        slotId: item.slotId,
        menuId: get(item.menu, 'id') || null,
        menuCount: get(item.slot, 'menuCount', 1),
        menu1Main: item.menu1Main,
        menu1Side: item.menu1Side,
        menu2Main: item.menu2Main,
        menu2Side: item.menu2Side
      }
      this.editDialog = !this.editDialog
    },
    goRegister(item) {
      const date = moment(item.date)
        .startOf('day')
        .toISOString()
      window.open(`/student-menu-register?date=${date}&slot=${item.slotId}`, '_blank')
    }
  }
}
</script>

<style scoped>
.v-data-table ::v-deep tr.day-start-row {
  background-color: #edf2f5;
}
.v-data-table ::v-deep tr.day-start-row:hover {
  background-color: #d6ebfc !important;
}
.v-data-table ::v-deep tr.today-row {
  background-color: #eee4c2;
}
.v-data-table ::v-deep tr.today-row:hover {
  background-color: #d6ebfc !important;
}
.v-data-table ::v-deep tr.today-row td {
  font-size: 0.95rem;
}
.v-data-table ::v-deep tr.today-row .caption.success--text {
  font-size: 0.95rem !important;
}
.v-data-table ::v-deep tr.today-row .caption.success--text .font-weight-bold {
  font-size: 1.3rem !important;
}

.v-data-table ::v-deep .caption.success--text .font-weight-bold {
  font-size: 1rem !important;
}

.dish-main {
  font-weight: 500;
}
.dish-side {
  font-weight: 300;
  font-size: 0.75rem;
}

/* ===== Mobile card layout ===== */
.mb-day {
  background-color: #edf2f5;
  border-radius: 4px;
}
.mb-day.mb-today {
  background-color: #eee4c2;
}
.mb-card-today {
  border: 2px solid #e0c97f !important;
  background-color: #fffdf5;
}
.mb-count {
  background: #f1f8f2;
  border-radius: 8px;
  padding: 10px;
}
.mb-count-num {
  font-size: 2.2rem;
  font-weight: 700;
  line-height: 1.1;
  color: #2e7d32;
}
</style>
